
import { Component, Vue } from "vue-property-decorator";
import { uiStore, configStore, documentStore } from "@/store/store";
import OnCanvasDragHandle from "./OnCanvasDragHandle.vue";
import { getNodeParent } from "@/lib/tree/tree";
import {
  getNodeElementWrapperStyles,
  getDomElementFromTreeNode,
  getNodeTitlebarStyles
} from "@/lib/canvas/helpers";
import { forceNextTick } from "@/util/force-next-tick";
import { getRightClickMenuPositionInCanvas } from "@/lib/right-click/actions";
import Icon from "@/components/UI/Icon.vue";
import { SECTION_SLUG } from "@/lib/elements/constants";
import lodash from "lodash";
import { keyboardShortcuts } from "@/lib/keyboard-shortcuts/keyboard-shortcuts";
import { KEYBOARD_SHORTCUTS } from "@/lib/keyboard-shortcuts/global-shortcuts";
import { IconName } from "@/lib/icons/icons";
import { getElementIcon, getElementName } from "@/lib/elements/utils";

@Component({
  components: {
    Icon,
    OnCanvasDragHandle
  }
})
export default class NodeTitlebar extends Vue {
  interval!: NodeJS.Timer;
  refreshRate = 500;

  unsubscribeFromStorePositionWrapper = () => {};
  nextTickPositionsWrapper() {
    forceNextTick(this.calculateStylesAndTitle);
  }

  openContextMenu(e: MouseEvent) {
    // don't reach the event in the menu, otherwise It'll select the wrong element
    e.stopPropagation();
    e.preventDefault();

    const position = getRightClickMenuPositionInCanvas(e.clientX, e.clientY);

    if (!this.elementId || !position) return;

    uiStore.setOnCanvasRightClickMenuState({
      elementId: this.elementId,
      position
    });
  }

  get builderMode() {
    return configStore.builderMode;
  }

  mounted() {
    this.calculateStylesAndTitle();

    this.unsubscribeFromStorePositionWrapper = this.$store.subscribe(
      this.nextTickPositionsWrapper
    );

    window.addEventListener("resize", this.nextTickPositionsWrapper);

    this.interval = setInterval(
      this.nextTickPositionsWrapper,
      this.refreshRate
    );

    keyboardShortcuts.register(KEYBOARD_SHORTCUTS.SHIFT_ESC, () => {
      this.activateParent();
    });
  }

  beforeDestroy() {
    this.unsubscribeFromStorePositionWrapper();

    window.removeEventListener("resize", this.nextTickPositionsWrapper);

    clearInterval(this.interval);

    keyboardShortcuts.unbind(KEYBOARD_SHORTCUTS.SHIFT_ESC);
  }

  get isDraftSection() {
    if (!this.element) return false;

    if (this.element.data.type !== SECTION_SLUG) return false;

    const isDraft = lodash.get(
      this.element.data.properties,
      "settings.advanced.draft"
    );

    return !!isDraft;
  }

  get isDragAndDropEnabled() {
    return uiStore.hasFullAccess;
  }

  get isElementUpdating() {
    if (!this.elementId) return false;
    return !!uiStore.elementsThatAreUpdating[this.elementId];
  }

  titlebarPositionStyle: { [key: string]: string } = {};

  wrapperPositionStyle: { [key: string]: string } = {
    display: "none"
  };

  elementTitle = "";

  // Everything is calculated in a func to avoid UI flicker caused by values being calculated at different times
  calculateStylesAndTitle() {
    if (uiStore.elementWithContentEditable || !this.element) {
      this.wrapperPositionStyle = {
        display: "none"
      };

      return;
    }

    this.elementTitle = this.getElementTitle();

    this.wrapperPositionStyle = getNodeElementWrapperStyles(
      getDomElementFromTreeNode(this.element)
    );

    this.titlebarPositionStyle = getNodeTitlebarStyles(this.element);
  }

  get titlebarClasses() {
    return {
      "node-titlebar-is-dragging": this.isDragging,
      "node-titlebar-is-draft-section": this.isDraftSection
    };
  }

  get isDragging() {
    return uiStore.currentlyDraggingCanvasElementId === this.elementId;
  }

  get element() {
    return uiStore.activeElement;
  }

  get elementId() {
    return uiStore.activeElementId;
  }

  get elementIcon(): IconName {
    if (!this.element) return "MaximizeIcon";
    return getElementIcon(this.element, true);
  }

  getElementTitle() {
    if (!this.element) return "";
    return getElementName(this.element);
  }

  duplicate() {
    if (this.elementId) {
      documentStore.duplicateElementAndActivateIt(this.elementId);
    }
  }

  remove() {
    if (this.elementId !== null) {
      documentStore.deleteElement({ nodeId: this.elementId });
    }
  }

  refreshSsr() {
    uiStore.setRefreshSsrElementId(this.elementId);
  }

  get hasSSr() {
    const activeElementBase =
      this.element && configStore.element(this.element.data.type);

    if (!activeElementBase) {
      return "";
    }

    return activeElementBase.template.includes("%%SSR%%");
  }

  get hasParent() {
    return this.parentElement && this.parentElement.data.type !== "root";
  }

  get node() {
    // False should never happen
    return this.element || false;
  }

  activateParent() {
    if (this.parentElement) {
      uiStore.activateElement(this.parentElement.id);
    }
  }

  get parentElement() {
    if (!this.element) return false;

    const maybeParent = getNodeParent(
      documentStore.document.tree,
      this.element.id
    );

    return maybeParent ? maybeParent : false;
  }
}
